<script lang="ts">
    import {getInjector, getLocalization} from 'core/svelte-context/context';
    import KpAvailabilityButton from '../../loan/kp-availability-button/KpAvailabilityButton.svelte';
    import {pipe} from 'core/utils';
    import {loc} from 'shared/utils/pipes';
    import {KpExemplarsListPresenter} from './kp-exemplars-list.presenter';
    import type ExemplarFilter from '../exemplar-filter';
    import type {Document, LabeledIdentified, ViewableExemplar} from 'typings/portaro.be.types';
    import KpGenericTable from 'shared/ui-widgets/table/KpGenericTable.svelte';
    import KpButton from 'shared/ui-widgets/button/KpButton.svelte';
    import {exists} from 'shared/utils/custom-utils';
    import type {MoreShowableViewableExemplar} from './types';
    import ExemplarPlacement from './components/ExemplarPlacement.svelte';
    import PropertyCell from './components/PropertyCell.svelte';
    import IconedContent from 'shared/ui-widgets/uicons/IconedContent.svelte';

    export let exemplars: ViewableExemplar[];
    export let properties: LabeledIdentified<string>[];
    export let document: Document;
    export let exemplarFilter: ExemplarFilter = null;

    const localize = getLocalization();
    const injector = getInjector();
    const presenter = injector.getByToken<KpExemplarsListPresenter>(KpExemplarsListPresenter.presenterName);

    let filteredProperties: LabeledIdentified<string>[];

    let availabilityAwareExemplars: MoreShowableViewableExemplar[];
    $: availabilityAwareExemplars = exemplars.map((exemplar) => ({...exemplar, showMore: false}));

    $: if (exists(availabilityAwareExemplars) && availabilityAwareExemplars.length !== 0) {
        filteredProperties = presenter.filterProperties(properties, availabilityAwareExemplars);
    }

    $: available = availabilityAwareExemplars.map((value) => value.availability).some((availability) => {
        return availability && availability.reasonable && (availability.capabilities.length > 0)
    });

    $: displayEditColumn = exists(availabilityAwareExemplars.find((exemplar) => {
        return exemplar.editable || exemplar.discardable || exemplar.discardionRestorable || exemplar.deletable;
    }));

    function showMore(exemplar: MoreShowableViewableExemplar): void {
        presenter.showMore(exemplar);
        availabilityAwareExemplars = [...availabilityAwareExemplars];
    }
</script>

<div class="exemplars-list">
    {#if exists(availabilityAwareExemplars)}
        {#if availabilityAwareExemplars.length === 0}
            <div class="panel-body text-center text-muted">
                {localize(/* @kp-localization detail.ZadneExemplareKDispozici */  'detail.ZadneExemplareKDispozici')}
            </div>
        {:else}
            <KpGenericTable additionalClasses="exemplar-table"
                            stripedRows
                            hoverRows
                            colorAccented
                            horizontallyDivided
                            responsive
                            columnHeadersCentered>

                <tr slot="header">
                    {#each filteredProperties as property}
                        <th class="col_{property.id}">{pipe(property, loc()) ?? ''}</th>
                    {/each}

                    {#if available}
                        <th class="col_loanRequest">{localize(/* @kp-localization loan.Vypujcit */ 'loan.Vypujcit')}</th>
                    {/if}

                    {#if displayEditColumn}
                        <th class="col_edit">
                            {localize(/* @kp-localization commons.Options */ 'commons.Options')}
                        </th>
                    {/if}
                </tr>

                <svelte:fragment slot="body">
                    {#each availabilityAwareExemplars as exemplar}
                        <tr class:text-muted={!(exemplarFilter?.anonymous(exemplar) ?? false)}
                            data-exemplar-id={exemplar.id}
                            title="{localize(/* @kp-localization exemplar.signature */ 'exemplar.signature')}:{exemplar.signature ?? ''},  {localize(/* @kp-localization exemplar.barCode */ 'exemplar.barCode')}:{exemplar.barCode ?? ''},  {localize(/* @kp-localization exemplar.accessNumber */ 'exemplar.accessNumber')}:{exemplar.accessNumber ?? ''}">

                            {#each filteredProperties as property}
                                <PropertyCell {exemplar} {property} {document} on:show-more={() => showMore(exemplar)}/>
                            {/each}

                            {#if available}
                                <td class="col_loanRequest">
                                    <KpAvailabilityButton on:click={() => presenter.requestExemplar(exemplar)}
                                                          availability={exemplar.availability}
                                                          forExemplar={true}
                                                          size="xs"
                                                          block={false}/>
                                </td>
                            {/if}

                            {#if displayEditColumn}
                                <td class="col_edit">
                                    <div class="action-buttons-container">
                                        {#if exemplar.editable}
                                            <KpButton buttonSize="xs"
                                                      isBlock="{true}"
                                                      on:click={() => presenter.editExemplar(exemplar)}
                                                      data-qa="edit-exemplar-button">
                                                <IconedContent icon="edit">
                                                    {localize(/* @kp-localization commons.editovat */ 'commons.editovat')}
                                                </IconedContent>
                                            </KpButton>
                                        {/if}

                                        {#if exemplar.discardable}
                                            <KpButton buttonSize="xs"
                                                      on:click={() => presenter.discardExemplar(exemplar)}
                                                      isBlock="{true}"
                                                      data-qa="discard-exemplar-button">
                                                <IconedContent icon="cross-circle">
                                                    {localize(/* @kp-localization commons.vyradit */ 'commons.vyradit') }
                                                </IconedContent>
                                            </KpButton>
                                        {/if}

                                        {#if exemplar.discardionRestorable}
                                            <KpButton buttonSize="xs"
                                                      isBlock="{true}"
                                                      on:click={() => presenter.restoreExemplar(exemplar)}
                                                      data-qa="restore-exemplar-button">
                                                <IconedContent icon="time-past">
                                                    {localize(/* @kp-localization commons.Restore */ 'commons.Restore')}
                                                </IconedContent>
                                            </KpButton>
                                        {/if}

                                        {#if exemplar.deletable}
                                            <KpButton buttonSize="xs"
                                                      isBlock="{true}"
                                                      on:click={() => presenter.deleteExemplar(exemplar)}
                                                      data-qa="delete-exemplar-button">
                                                <IconedContent icon="trash">
                                                    {localize(/* @kp-localization commons.Smazat */ 'commons.Smazat')}
                                                </IconedContent>
                                            </KpButton>
                                        {/if}
                                    </div>
                                </td>
                            {/if}
                        </tr>

                        {#if exemplar.showMore}
                            <ExemplarPlacement exemplar={exemplar}/>
                        {/if}
                    {/each}
                </svelte:fragment>
            </KpGenericTable>
        {/if}
    {/if}
</div>

<style lang="less">
    @import (reference) "bootstrap-less/bootstrap/variables";

    :global {
        .exemplars-list .exemplar-table {
            th {
                height: 42px;
            }

            td, th {
                font-size: 13px;
                padding: @padding-base-vertical @padding-small-horizontal;
                vertical-align: middle;
                text-align: center;
            }

            tr:last-child td {
                border-bottom: none;
            }
        }
    }

    .action-buttons-container {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
    }
</style>